import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  Float32BufferAttribute,
  Matrix4,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const UP = new Vector3(0, 1, 0);

/**
 * Collects primitives with a flat colour each and bakes them into one
 * vertex-coloured geometry, so a whole roadside stall costs a single draw call.
 *
 * Transforms nest: `group` pushes a yaw rotation and an offset, so a figure or
 * a crate can be built in its own local space and dropped anywhere in the
 * scene. `yaw` rotates about Y only — enough for everything that stands on the
 * ground, which is everything here.
 */
export class Parts {
  constructor() {
    this.geometries = [];
    this.stack = [new Matrix4()];
  }

  get matrix() {
    return this.stack[this.stack.length - 1];
  }

  group(x, y, z, yaw, build) {
    const local = new Matrix4().makeRotationY(yaw);
    local.setPosition(x, y, z);
    this.stack.push(new Matrix4().multiplyMatrices(this.matrix, local));
    build();
    this.stack.pop();
  }

  box(width, height, depth, color, x, y, z) {
    const geometry = new BoxGeometry(width, height, depth);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  cyl(radiusTop, radiusBottom, height, segments, color, x, y, z, rotation = null) {
    const geometry = new CylinderGeometry(radiusTop, radiusBottom, height, segments);
    if (rotation?.x) geometry.rotateX(rotation.x);
    if (rotation?.y) geometry.rotateY(rotation.y);
    if (rotation?.z) geometry.rotateZ(rotation.z);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  sphere(radius, widthSegments, heightSegments, color, x, y, z) {
    const geometry = new SphereGeometry(radius, widthSegments, heightSegments);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  /**
   * A limb between two points. Cylinders are authored along +Y, so the geometry
   * is rotated onto the segment first — that is how the kumsary's arms and legs
   * are posed without hand-computing Euler angles.
   */
  beam(ax, ay, az, bx, by, bz, radius, color, segments = 6) {
    const direction = new Vector3(bx - ax, by - ay, bz - az);
    const length = direction.length();
    if (length < 1e-5) return;
    const geometry = new CylinderGeometry(radius, radius, length, segments);
    geometry.applyQuaternion(
      new Quaternion().setFromUnitVectors(UP, direction.normalize()),
    );
    geometry.translate((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
    this.#push(geometry, color);
  }

  merge() {
    if (!this.geometries.length) return null;
    const merged = mergeGeometries(this.geometries, false);
    for (const geometry of this.geometries) geometry.dispose();
    this.geometries.length = 0;
    return merged;
  }

  #push(geometry, color) {
    geometry.applyMatrix4(this.matrix);

    const flat = geometry.index ? geometry.toNonIndexed() : geometry;
    if (flat !== geometry) geometry.dispose();

    const count = flat.attributes.position.count;
    const colors = new Float32Array(count * 3);
    const parsed = new Color(color);
    for (let i = 0; i < count; i += 1) {
      colors[i * 3] = parsed.r;
      colors[i * 3 + 1] = parsed.g;
      colors[i * 3 + 2] = parsed.b;
    }
    flat.setAttribute('color', new Float32BufferAttribute(colors, 3));

    this.geometries.push(flat);
  }
}
